// src/components/blog/BlogPostDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchBlogPost,
  deleteBlogPost,
  updateBlogPostStatus,
  fetchBlogComments,
  updateCommentStatus,
  deleteComment,
  clearBlogPostError,
} from '../../store/actions/blogPostActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';
import Pagination from '../common/Pagination';

const BlogPostDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    selectedBlogPost,
    loading,
    error,
    comments,
    commentsMeta,
  } = useSelector((state) => state.blogPosts);

  const [activeTab, setActiveTab] = useState('content');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [commentsPage, setCommentsPage] = useState(1);
  const [selectedComment, setSelectedComment] = useState(null);
  const [showCommentDeleteConfirm, setShowCommentDeleteConfirm] = useState(false);
  const [showCommentStatusModal, setShowCommentStatusModal] = useState(false);
  const [commentStatus, setCommentStatus] = useState('');

  const statusOptions = [
    { value: 'DRAFT', label: 'Brouillon', color: '#6b7280', bg: '#f3f4f6' },
    { value: 'PENDING_REVIEW', label: 'En attente de relecture', color: '#f59e0b', bg: '#fef3c7' },
    { value: 'PUBLISHED', label: 'Publié', color: '#10b981', bg: '#d1fae5' },
    { value: 'SCHEDULED', label: 'Programmé', color: '#3b82f6', bg: '#dbeafe' },
    { value: 'ARCHIVED', label: 'Archivé', color: '#6b7280', bg: '#e5e7eb' },
  ];

  const commentStatusOptions = [
    { value: 'PENDING', label: 'En attente', color: '#f59e0b', bg: '#fef3c7' },
    { value: 'APPROVED', label: 'Approuvé', color: '#10b981', bg: '#d1fae5' },
    { value: 'REJECTED', label: 'Rejeté', color: '#dc2626', bg: '#fee2e2' },
    { value: 'SPAM', label: 'Spam', color: '#6b7280', bg: '#f3f4f6' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      textDecoration: 'none',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    commentsButton: {
      backgroundColor: '#0891b2',
      color: '#ffffff',
    },
    commentsButtonHover: {
      backgroundColor: '#0e7490',
    },
    statusButton: {
      backgroundColor: '#f59e0b',
      color: '#ffffff',
    },
    statusButtonHover: {
      backgroundColor: '#d97706',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'flex-start',
    },
    featuredImage: {
      width: '200px',
      height: '150px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      backgroundColor: '#e5e7eb',
    },
    featuredImagePlaceholder: {
      width: '200px',
      height: '150px',
      borderRadius: '0.5rem',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '3rem',
      color: '#9ca3af',
    },
    postInfo: {
      flex: 1,
    },
    postTitle: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    postMeta: {
      display: 'flex',
      gap: '2rem',
      marginTop: '1rem',
      flexWrap: 'wrap',
    },
    metaItem: {
      display: 'flex',
      flexDirection: 'column',
    },
    metaLabel: {
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      color: '#6b7280',
      marginBottom: '0.25rem',
    },
    metaValue: {
      fontSize: '1rem',
      fontWeight: 500,
      color: '#111827',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.5rem 1rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    featuredBadge: {
      backgroundColor: '#fbbf24',
      color: '#92400e',
      marginLeft: '1rem',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1.5rem',
      marginTop: '2rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    tabContent: {
      padding: '2rem',
    },
    articleContent: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    excerpt: {
      fontSize: '1.125rem',
      fontStyle: 'italic',
      color: '#4b5563',
      marginBottom: '2rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #2563eb',
    },
    content: {
      fontSize: '1rem',
      lineHeight: '1.8',
      color: '#374151',
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    galleryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: '1rem',
      marginTop: '1rem',
    },
    galleryImage: {
      width: '100%',
      height: '150px',
      borderRadius: '0.5rem',
      objectFit: 'cover',
      cursor: 'pointer',
      transition: 'transform 0.2s',
    },
    seoSection: {
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      padding: '1.5rem',
      marginTop: '2rem',
    },
    seoTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    seoItem: {
      display: 'flex',
      marginBottom: '0.75rem',
    },
    seoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    seoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    keywords: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    keyword: {
      padding: '0.25rem 0.5rem',
      backgroundColor: '#e5e7eb',
      color: '#374151',
      borderRadius: '0.25rem',
      fontSize: '0.75rem',
    },
    commentsSection: {
      marginTop: '1rem',
    },
    commentsHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
    },
    commentsTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      margin: 0,
    },
    commentFilters: {
      display: 'flex',
      gap: '0.5rem',
    },
    commentFilterButton: {
      padding: '0.25rem 0.75rem',
      background: 'none',
      border: '1px solid #e5e7eb',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      cursor: 'pointer',
      color: '#374151',
    },
    commentFilterButtonActive: {
      backgroundColor: '#2563eb',
      color: '#ffffff',
      borderColor: '#2563eb',
    },
    commentCard: {
      padding: '1rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      backgroundColor: '#ffffff',
    },
    commentHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '0.5rem',
    },
    commentAuthor: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    commentAvatar: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    commentAuthorInfo: {
      display: 'flex',
      flexDirection: 'column',
    },
    commentAuthorName: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: '#111827',
    },
    commentAuthorEmail: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    commentDate: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    commentContent: {
      fontSize: '0.875rem',
      color: '#374151',
      lineHeight: '1.6',
      marginBottom: '0.75rem',
    },
    commentFooter: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    commentStatus: {
      display: 'inline-flex',
      padding: '0.125rem 0.5rem',
      fontSize: '0.625rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    commentActions: {
      display: 'flex',
      gap: '0.5rem',
    },
    commentActionButton: {
      padding: '0.25rem 0.5rem',
      background: 'none',
      border: 'none',
      fontSize: '0.75rem',
      cursor: 'pointer',
      color: '#6b7280',
      borderRadius: '0.25rem',
      transition: 'all 0.2s',
    },
    statusModal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    statusModalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      padding: '2rem',
      maxWidth: '400px',
      width: '90%',
    },
    statusModalTitle: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    statusOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      marginBottom: '2rem',
    },
    statusOption: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.75rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    statusOptionSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    statusOptionRadio: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    modalButtons: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    saveButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  const getStatusStyle = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return {
      backgroundColor: option?.bg || '#f3f4f6',
      color: option?.color || '#374151',
    };
  };

  const getCommentStatusStyle = (status) => {
    const option = commentStatusOptions.find(s => s.value === status);
    return {
      backgroundColor: option?.bg || '#f3f4f6',
      color: option?.color || '#374151',
    };
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchBlogPost(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (id && activeTab === 'comments') {
      dispatch(fetchBlogComments(id, { page: commentsPage, limit: 10 }));
    }
  }, [dispatch, id, activeTab, commentsPage]);

  useEffect(() => {
    if (selectedBlogPost) {
      setNewStatus(selectedBlogPost.status);
    }
  }, [selectedBlogPost]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteBlogPost(id));
      navigate('/admin/blog-posts');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const handleStatusUpdate = useCallback(async () => {
    try {
      await dispatch(updateBlogPostStatus(id, newStatus));
      setShowStatusModal(false);
    } catch (error) {
      console.error('Status update failed:', error);
    }
  }, [dispatch, id, newStatus]);

  const handleCommentStatusUpdate = useCallback(async () => {
    try {
      await dispatch(updateCommentStatus(id, selectedComment.id, commentStatus));
      setShowCommentStatusModal(false);
      setSelectedComment(null);
      setCommentStatus('');
      // Refresh comments
      dispatch(fetchBlogComments(id, { page: commentsPage, limit: 10 }));
    } catch (error) {
      console.error('Comment status update failed:', error);
    }
  }, [dispatch, id, selectedComment, commentStatus, commentsPage]);

  const handleCommentDelete = useCallback(async () => {
    try {
      await dispatch(deleteComment(id, selectedComment.id));
      setShowCommentDeleteConfirm(false);
      setSelectedComment(null);
      // Refresh comments
      dispatch(fetchBlogComments(id, { page: commentsPage, limit: 10 }));
    } catch (error) {
      console.error('Comment delete failed:', error);
    }
  }, [dispatch, id, selectedComment, commentsPage]);

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const tabs = [
    { id: 'content', label: 'Contenu' },
    { id: 'gallery', label: `Galerie (${selectedBlogPost?.galleryImages?.length || 0})` },
    { id: 'seo', label: 'SEO' },
    { id: 'comments', label: `Commentaires (${selectedBlogPost?._count?.comments || 0})` },
  ];

  if (loading && !selectedBlogPost) {
    return <LoadingSpinner fullScreen />;
  }

  if (!selectedBlogPost && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Article non trouvé"
        />
        <Link to="/admin/blog-posts" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/blog-posts')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails de l'article</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/blog-posts/${id}/comments`}
            style={{
              ...styles.actionButton,
              ...styles.commentsButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.commentsButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.commentsButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Commentaires
          </Link>
          <Link
            to={`/admin/blog-posts/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={() => setShowStatusModal(true)}
            style={{
              ...styles.actionButton,
              ...styles.statusButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.statusButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.statusButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Statut
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearBlogPostError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            {selectedBlogPost.featuredImage ? (
              <img
                src={selectedBlogPost.featuredImage.storagePath}
                alt={selectedBlogPost.title}
                style={styles.featuredImage}
              />
            ) : (
              <div style={styles.featuredImagePlaceholder}>
                📝
              </div>
            )}
            <div style={styles.postInfo}>
              <h2 style={styles.postTitle}>
                {selectedBlogPost.title}
                {selectedBlogPost.isFeatured && (
                  <span style={{ ...styles.badge, ...styles.featuredBadge }}>
                    ★ À la une
                  </span>
                )}
              </h2>
              <div style={styles.postMeta}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Statut</span>
                  <span
                    style={{
                      ...styles.badge,
                      ...getStatusStyle(selectedBlogPost.status),
                    }}
                  >
                    {statusOptions.find(s => s.value === selectedBlogPost.status)?.label || selectedBlogPost.status}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Auteur</span>
                  <span style={styles.metaValue}>
                    {selectedBlogPost.author ? `${selectedBlogPost.author.firstName} ${selectedBlogPost.author.lastName}` : '—'}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Catégorie</span>
                  <span style={styles.metaValue}>
                    {selectedBlogPost.category?.name || '—'}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Date de publication</span>
                  <span style={styles.metaValue}>
                    {formatDate(selectedBlogPost.publishedAt || selectedBlogPost.scheduledAt || selectedBlogPost.createdAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Vues</div>
              <div style={styles.statValue}>{selectedBlogPost.views || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Commentaires</div>
              <div style={styles.statValue}>{selectedBlogPost._count?.comments || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Temps de lecture</div>
              <div style={styles.statValue}>{selectedBlogPost.readTime || '—'} min</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Mots</div>
              <div style={styles.statValue}>{selectedBlogPost.wordCount || 0}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'content' && (
            <div style={styles.articleContent}>
              {selectedBlogPost.excerpt && (
                <div style={styles.excerpt}>
                  {selectedBlogPost.excerpt}
                </div>
              )}
              <div
                style={styles.content}
                dangerouslySetInnerHTML={{ __html: selectedBlogPost.content }}
              />
            </div>
          )}

          {activeTab === 'gallery' && (
            <div>
              {selectedBlogPost.galleryImages && selectedBlogPost.galleryImages.length > 0 ? (
                <div style={styles.galleryGrid}>
                  {selectedBlogPost.galleryImages.map((image) => (
                    <img
                      key={image.id}
                      src={image.storagePath}
                      alt={image.caption || selectedBlogPost.title}
                      style={styles.galleryImage}
                      onClick={() => window.open(image.storagePath, '_blank')}
                    />
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  Aucune image dans la galerie
                </div>
              )}
            </div>
          )}

          {activeTab === 'seo' && (
            <div>
              <div style={styles.seoSection}>
                <h3 style={styles.seoTitle}>Meta données</h3>
                <div style={styles.seoItem}>
                  <span style={styles.seoLabel}>Meta titre</span>
                  <span style={styles.seoValue}>{selectedBlogPost.metaTitle || '—'}</span>
                </div>
                <div style={styles.seoItem}>
                  <span style={styles.seoLabel}>Meta description</span>
                  <span style={styles.seoValue}>{selectedBlogPost.metaDescription || '—'}</span>
                </div>
                <div style={styles.seoItem}>
                  <span style={styles.seoLabel}>URL canonique</span>
                  <span style={styles.seoValue}>{selectedBlogPost.canonicalUrl || '—'}</span>
                </div>
                <div style={styles.seoItem}>
                  <span style={styles.seoLabel}>Mots-clés</span>
                  <div style={styles.keywords}>
                    {selectedBlogPost.keywords && selectedBlogPost.keywords.length > 0 ? (
                      selectedBlogPost.keywords.map((keyword, index) => (
                        <span key={index} style={styles.keyword}>
                          {keyword}
                        </span>
                      ))
                    ) : (
                      <span style={styles.seoValue}>—</span>
                    )}
                  </div>
                </div>
              </div>

              {selectedBlogPost.ogImage && (
                <div style={styles.seoSection}>
                  <h3 style={styles.seoTitle}>Image Open Graph</h3>
                  <img
                    src={selectedBlogPost.ogImage.storagePath}
                    alt="OG Image"
                    style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '0.5rem' }}
                  />
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div style={styles.commentsSection}>
              <div style={styles.commentsHeader}>
                <h3 style={styles.commentsTitle}>
                  Commentaires ({selectedBlogPost._count?.comments || 0})
                </h3>
                <div style={styles.commentFilters}>
                  <button
                    style={styles.commentFilterButton}
                    onClick={() => dispatch(fetchBlogComments(id, { page: 1, limit: 10 }))}
                  >
                    Tous
                  </button>
                  <button
                    style={styles.commentFilterButton}
                    onClick={() => dispatch(fetchBlogComments(id, { page: 1, limit: 10, status: 'PENDING' }))}
                  >
                    En attente
                  </button>
                  <button
                    style={styles.commentFilterButton}
                    onClick={() => dispatch(fetchBlogComments(id, { page: 1, limit: 10, status: 'APPROVED' }))}
                  >
                    Approuvés
                  </button>
                </div>
              </div>

              {comments && comments.length > 0 ? (
                <>
                  {comments.map((comment) => (
                    <div key={comment.id} style={styles.commentCard}>
                      <div style={styles.commentHeader}>
                        <div style={styles.commentAuthor}>
                          <div style={styles.commentAvatar}>
                            {comment.authorName?.charAt(0).toUpperCase()}
                          </div>
                          <div style={styles.commentAuthorInfo}>
                            <span style={styles.commentAuthorName}>
                              {comment.authorName}
                            </span>
                            <span style={styles.commentAuthorEmail}>
                              {comment.authorEmail}
                            </span>
                          </div>
                        </div>
                        <span style={styles.commentDate}>
                          {formatDate(comment.createdAt)}
                        </span>
                      </div>

                      <div style={styles.commentContent}>
                        {comment.content}
                      </div>

                      <div style={styles.commentFooter}>
                        <span
                          style={{
                            ...styles.commentStatus,
                            ...getCommentStatusStyle(comment.status),
                          }}
                        >
                          {commentStatusOptions.find(s => s.value === comment.status)?.label || comment.status}
                        </span>
                        <div style={styles.commentActions}>
                          {comment.status !== 'APPROVED' && (
                            <button
                              style={styles.commentActionButton}
                              onClick={() => {
                                setSelectedComment(comment);
                                setCommentStatus('APPROVED');
                                setShowCommentStatusModal(true);
                              }}
                            >
                              Approuver
                            </button>
                          )}
                          {comment.status !== 'REJECTED' && (
                            <button
                              style={styles.commentActionButton}
                              onClick={() => {
                                setSelectedComment(comment);
                                setCommentStatus('REJECTED');
                                setShowCommentStatusModal(true);
                              }}
                            >
                              Rejeter
                            </button>
                          )}
                          {comment.status !== 'SPAM' && (
                            <button
                              style={styles.commentActionButton}
                              onClick={() => {
                                setSelectedComment(comment);
                                setCommentStatus('SPAM');
                                setShowCommentStatusModal(true);
                              }}
                            >
                              Spam
                            </button>
                          )}
                          <button
                            style={{ ...styles.commentActionButton, color: '#dc2626' }}
                            onClick={() => {
                              setSelectedComment(comment);
                              setShowCommentDeleteConfirm(true);
                            }}
                          >
                            Supprimer
                          </button>
                        </div>
                      </div>

                      {comment.user && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#6b7280' }}>
                          Utilisateur connecté: {comment.user.firstName} {comment.user.lastName}
                        </div>
                      )}
                    </div>
                  ))}

                  <Pagination
                    currentPage={commentsMeta.page}
                    totalPages={commentsMeta.totalPages}
                    onPageChange={setCommentsPage}
                  />
                </>
              ) : (
                <div style={styles.emptyState}>
                  Aucun commentaire pour cet article
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* selectedBlogPost Status Update Modal */}
      {showStatusModal && (
        <div style={styles.statusModal} onClick={() => setShowStatusModal(false)}>
          <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.statusModalTitle}>
              Changer le statut de l'article
            </h3>
            <div style={styles.statusOptions}>
              {statusOptions.map((option) => (
                <div
                  key={option.value}
                  style={{
                    ...styles.statusOption,
                    ...(newStatus === option.value ? styles.statusOptionSelected : {}),
                  }}
                  onClick={() => setNewStatus(option.value)}
                >
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={newStatus === option.value}
                    onChange={() => setNewStatus(option.value)}
                    style={styles.statusOptionRadio}
                  />
                  <span style={{ flex: 1 }}>{option.label}</span>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: option.bg,
                      color: option.color,
                      fontSize: '0.75rem',
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={newStatus === selectedBlogPost.status}
                style={{
                  ...styles.saveButton,
                  opacity: newStatus === selectedBlogPost.status ? 0.5 : 1,
                  cursor: newStatus === selectedBlogPost.status ? 'not-allowed' : 'pointer',
                }}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Status Update Modal */}
      {showCommentStatusModal && (
        <div style={styles.statusModal} onClick={() => setShowCommentStatusModal(false)}>
          <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.statusModalTitle}>
              Changer le statut du commentaire
            </h3>
            <div style={styles.statusOptions}>
              {commentStatusOptions.map((option) => (
                <div
                  key={option.value}
                  style={{
                    ...styles.statusOption,
                    ...(commentStatus === option.value ? styles.statusOptionSelected : {}),
                  }}
                  onClick={() => setCommentStatus(option.value)}
                >
                  <input
                    type="radio"
                    name="commentStatus"
                    value={option.value}
                    checked={commentStatus === option.value}
                    onChange={() => setCommentStatus(option.value)}
                    style={styles.statusOptionRadio}
                  />
                  <span style={{ flex: 1 }}>{option.label}</span>
                  <span
                    style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      backgroundColor: option.bg,
                      color: option.color,
                      fontSize: '0.75rem',
                    }}
                  >
                    {option.label}
                  </span>
                </div>
              ))}
            </div>
            <div style={styles.modalButtons}>
              <button
                onClick={() => setShowCommentStatusModal(false)}
                style={styles.cancelButton}
              >
                Annuler
              </button>
              <button
                onClick={handleCommentStatusUpdate}
                disabled={commentStatus === selectedComment?.status}
                style={{
                  ...styles.saveButton,
                  opacity: commentStatus === selectedComment?.status ? 0.5 : 1,
                  cursor: commentStatus === selectedComment?.status ? 'not-allowed' : 'pointer',
                }}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Delete Confirmation */}
      <ConfirmDialog
        isOpen={showCommentDeleteConfirm}
        onClose={() => {
          setShowCommentDeleteConfirm(false);
          setSelectedComment(null);
        }}
        onConfirm={handleCommentDelete}
        title="Supprimer le commentaire"
        message="Êtes-vous sûr de vouloir supprimer ce commentaire ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />

      {/* Post Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer l'article"
        message={`Êtes-vous sûr de vouloir supprimer l'article "${selectedBlogPost?.title}" ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default BlogPostDetails;